import React, { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { paths } from './paths';

export interface RouteGuardProps {
  children: ReactNode;
}

function RouteLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

/** UX gate only; Firestore/Storage/callable authorization remains authoritative. */
export function RequireAuth({ children }: RouteGuardProps) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) return <RouteLoading />;
  if (!currentUser) {
    return <Navigate to={paths.login(location.pathname + location.search)} replace />;
  }
  return <>{children}</>;
}

/** UX gate only; this does not replace server-side admin authorization. */
export function RequireAdmin({ children }: RouteGuardProps) {
  const { currentUser, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) return <RouteLoading />;
  if (!currentUser) {
    return <Navigate to={paths.login(location.pathname + location.search)} replace />;
  }
  if (!isAdmin) return <Navigate to={paths.queue()} replace />;
  return <>{children}</>;
}

export function NotFoundPage() {
  const { currentUser, loading } = useAuth();
  if (loading) return <RouteLoading />;
  const destination = currentUser ? paths.queue() : paths.login();
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center">
      <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">404</p>
      <h1 className="text-3xl font-semibold text-gray-900">Page not found</h1>
      <p className="max-w-md text-sm text-gray-600">
        That address is not part of the Workbench application.
      </p>
      <Link
        to={destination}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
      >
        {currentUser ? 'Back to queue' : 'Return to sign in'}
      </Link>
    </main>
  );
}
